"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getCountryCookie, COUNTRY_COOKIE_EVENT } from "@/lib/country-cookie";
import { getCountryByCode } from "@/lib/countries";

const BASE_TABS = [
  { label: "Regulatory", href: "/legal/regulatory" },
  { label: "Terms & Conditions", href: "/legal/terms-and-conditions" },
  { label: "Privacy Policy", href: "/legal/privacy-policy" },
  { label: "Cookie Policy", href: "/legal/cookie-policy" },
  { label: "Complaints Policy", href: "/legal/complaints-policy" },
  { label: "Promotional Terms", href: "/legal/promotional-offer-terms-and-conditions" },
  { label: "Personalised Ads", href: "/legal/personalised-ads" },
];

export default function LegalNav() {
  const pathname = usePathname();
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const check = () => setCountryCode(getCountryCookie());
    const timer = setTimeout(check, 0);
    window.addEventListener(COUNTRY_COOKIE_EVENT, check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(COUNTRY_COOKIE_EVENT, check);
    };
  }, []);

  const updateScrollButtons = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const scrollByAmount = (direction: 1 | -1) => {
    scrollerRef.current?.scrollBy({ left: direction * 240, behavior: "smooth" });
  };

  const activeCountry = getCountryByCode(countryCode);

  // Regulatory content differs by region, so its tab points at the country's version once
  // the visitor has chosen one — every other legal page is identical regardless of location.
  const tabs = BASE_TABS.map(({ label, href }) => ({
    label,
    href: activeCountry?.redirects?.[href] ?? href,
  }));

  // Longest-href-first so a more specific route wins over a parent prefix
  const activeHref = [...tabs]
    .sort((a, b) => b.href.length - a.href.length)
    .find((t) => pathname === t.href || pathname.startsWith(t.href + "/"))?.href;

  const showScrollControls = canScrollLeft || canScrollRight;

  return (
    <div className="px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
        <div
          ref={scrollerRef}
          className="min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <div className="flex min-w-max gap-0 xl:gap-4">
            {tabs.map((tab) => {
              const isActive = activeHref === tab.href;
              return (
                <Link
                  key={tab.href}
                  href={tab.href}
                  className={`shrink-0 px-5 py-3 text-xs font-semibold tracking-widest uppercase whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-brand-teal text-brand-text rounded-sm"
                      : "text-brand-card hover:bg-brand-card/10 rounded-sm"
                  }`}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </div>
        {showScrollControls && (
          <div className="flex items-center justify-center lg:justify-end gap-2 shrink-0">
            <button
              type="button"
              aria-label="Scroll tabs left"
              disabled={!canScrollLeft}
              onClick={() => scrollByAmount(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-card/10 text-brand-card hover:bg-brand-card/20 disabled:opacity-30 disabled:hover:bg-brand-card/10"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Scroll tabs right"
              disabled={!canScrollRight}
              onClick={() => scrollByAmount(1)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-card/10 text-brand-card hover:bg-brand-card/20 disabled:opacity-30 disabled:hover:bg-brand-card/10"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
